import { useCallback, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Flex, Text, useReducedMotion } from '@nula/core-ui';
import type { Proposal } from '@/lib/templates/api';
import { KEYBOARD_HINT } from '@/lib/templates/review-copy';
import { useTextReview, type TextReview } from '../hooks/use-text-review';
import { focusAfter, type Decision } from '../utils/review-cursor';
import { reviewKeyAction } from '../utils/review-keys';
import { adjacentChange, decisionFor, nextOpen } from '../utils/review-progress';
import { SurfaceReviewBar } from './surface-review-bar';
import { SurfaceReviewCards } from './surface-review-cards';
import styles from './surface-text-review.module.css';

type FocusTarget = { kind: 'card'; key: string } | { kind: 'primary' };

interface SurfaceTextReviewProps {
  entityId: string;
  surfaceId: string;
  proposal: Proposal;
  templateName: string | null;
  onClosed: (settled: boolean) => void;
  onReplaced: () => void;
  onPartiallyApplied: (proposalId: string) => void;
}

export function SurfaceTextReview({
  entityId,
  surfaceId,
  proposal,
  templateName,
  onClosed,
  onReplaced,
  onPartiallyApplied,
}: SurfaceTextReviewProps) {
  const review = useTextReview(entityId, surfaceId, proposal, {
    onClosed: () => onClosed(true),
    onReplaced,
    onPartiallyApplied,
  });
  const reduced = useRef(false);
  reduced.current = useReducedMotion();
  const [currentKey, setCurrentKey] = useState(() => nextOpen(review.state, proposal, null));
  const [focusRequest, setFocusRequest] = useState<FocusTarget | null>(null);
  const [round, setRound] = useState(review.state.round);
  const slots = useRef(new Map<string, HTMLElement>());
  const primaryRef = useRef<HTMLButtonElement>(null);

  const request = useCallback((target: FocusTarget | null) => {
    if (target === null) return;
    if (target.kind === 'card') setCurrentKey(target.key);
    setFocusRequest({ ...target });
  }, []);

  const [busy, setBusy] = useState(review.busy);
  if (busy !== review.busy) {
    setBusy(review.busy);
    if (busy && round === review.state.round) request({ kind: 'primary' });
  }

  const [reviewedId, setReviewedId] = useState(review.state.proposalId);
  if (reviewedId !== review.state.proposalId) {
    setReviewedId(review.state.proposalId);
    setCurrentKey(nextOpen(review.state, proposal, null));
    setRound(review.state.round);
  } else if (round !== review.state.round) {
    setRound(review.state.round);
    if (review.state.round === 'conflicts') {
      const first = nextOpen(review.state, proposal, null);
      if (first !== null) request({ kind: 'card', key: first });
    }
  }

  useLayoutEffect(() => {
    if (focusRequest === null) return;
    const el = focusRequest.kind === 'card' ? slots.current.get(focusRequest.key) : primaryRef.current;
    if (!el) return;
    el.focus();
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', behavior: reduced.current ? 'auto' : 'smooth' });
    }
  }, [focusRequest]);

  const cardsReview = useMemo<TextReview>(() => {
    const decide = (decision: Decision, apply: () => void) => {
      const target = focusAfter(review.state, proposal, decision);
      apply();
      request(target);
    };
    return {
      ...review,
      choose: (key, choice) => decide({ kind: 'choose', key, choice }, () => review.choose(key, choice)),
      chooseConflict: (key, choice) =>
        decide({ kind: 'conflict', key, choice }, () => review.chooseConflict(key, choice)),
    };
  }, [review, proposal, request]);

  const slotRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) slots.current.set(key, el);
    else slots.current.delete(key);
  }, []);

  const onKeyDown = (event: KeyboardEvent) => {
    if (review.busy) return;
    const action = reviewKeyAction(event.nativeEvent);
    if (action === null) return;
    if (action === 'next' || action === 'previous') {
      const key = adjacentChange(proposal, currentKey, action === 'next' ? 1 : -1);
      if (key === null) return;
      event.preventDefault();
      request({ kind: 'card', key });
      return;
    }
    const section = proposal.sections.find((candidate) => candidate.key === currentKey);
    if (!section) return;
    const decision = decisionFor(review.state, section, action);
    if (decision === null) return;
    event.preventDefault();
    if (decision.kind === 'choose') cardsReview.choose(section.key, decision.choice);
    else cardsReview.chooseConflict(section.key, decision.choice);
  };

  return (
    <Flex vertical data-surface-review="text" className={styles.root} onKeyDown={onKeyDown}>
      <SurfaceReviewBar
        proposal={proposal}
        review={review}
        templateName={templateName}
        onClose={() => onClosed(false)}
        primaryRef={primaryRef}
      />
      <Flex vertical className={styles.scroll}>
        <Flex vertical className={styles.column}>
          <SurfaceReviewCards
            proposal={proposal}
            review={cardsReview}
            currentKey={currentKey}
            slotRef={slotRef}
            onSlotFocus={setCurrentKey}
          />
          <Text type="secondary" size="sm" className={styles.hint}>
            {KEYBOARD_HINT}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
}
